import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import LoaderCard from '../loader-card/LoaderCard';

@Component<MiFileUpload>({
  components: {
    LoaderCard,
  }
})
export default class MiFileUpload extends Vue {
  @Prop()
  protected acceptedFileTypes?: string[];

  @Prop()
  protected filePath?: string;

  @Prop({default: false})
  protected clearAfterUpload?: boolean;

  @Prop({default: false})
  protected multiple?: boolean;

  protected accepted: string [] = [];
  protected filePathClone: string | null = null;
  protected dragging: boolean = false;
  protected hovering: boolean = false;
  protected isUploading: boolean = false;
  protected isUploaded: boolean = false;
  protected invalidFileType: boolean = false;
  protected showRipple: boolean = false;
  protected style: RippleStyle = {};

  protected imageExtesions = ['jpg', 'jpeg', 'png', 'svg', 'gif', 'bmp'];
  protected fileIcons: {[key:string]:string} = {
    pdf: 'mdi-file-pdf-box',
    xls: 'mdi-file-excel-box',
    xlsx: 'mdi-file-excel-box',
    docx: 'mdi-file-word-box',
    doc: 'mdi-file-word-box',
    ppt: 'mdi-file-powerpoint-box',
    pptx: 'mdi-file-powerpoint-box',
    other: 'mdi-file-document-box',
  };

  protected mounted() {
    if(!this.acceptedFileTypes) {
      this.accepted = [
        'image/png',
        'image/jpeg',
        'image/svg+xml',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/pdf',
      ];
    } else {
      this.accepted = this.acceptedFileTypes;
    }
  }

  protected onFileInputChange() {
    if(this.invalidFileType) {
      this.reset();
      return;
    }

    const input = this.$refs.hiddenFileInput as HTMLInputElement;
    if (this.multiple) {
      if (input.files) {
        const files: File[] = Array.from(input.files);
        if(files) {
          this.isUploading = true;
          files.forEach((file: File) => {
            this.$emit('itemDropped', file);
          });
        }
      }
    } else {
      const files: FileList | null = input.files;

      if(files) {
        this.isUploading = true;
        this.$emit('itemDropped', files[0]);
      }
    }
  }

  private get fileNameFromFilePath() {
    if(!this.filePathClone) {
      return '';
    }

    const parsedPath = this.filePathClone.split('/').pop();
    if(!parsedPath) {
      return '';
    }
    return parsedPath.split('?')[0];
  }

  private get fileExtension() {
    return this.fileNameFromFilePath ? this.fileNameFromFilePath.split('.').pop() : null;
  }

  protected get iconClass() {
    if(!this.fileExtension || !this.fileIcons[this.fileExtension]) {
      return this.fileIcons.other;
    }

    return this.fileIcons[this.fileExtension];
  }

  protected get isImage() {
    if(!this.filePathClone) {
      return false;
    }

    if(!this.fileExtension) {
      return false;
    }

    return this.imageExtesions.includes(this.fileExtension) || this.filePathClone.includes('data:image');
  }

  protected handleUploadContainerClick(event: any) {
    const input = this.$refs.hiddenFileInput as HTMLInputElement;
    if(input) {
      const event = new MouseEvent('click');
      input.dispatchEvent(event);
    }
  }

  protected get uploadMessage() {
    if(!this.dragging) {
      if(this.hovering) {
        return `Click to select a file`;
      }
      return `Drag and drop a file to upload`;
    } else {
      if(this.invalidFileType) {
        return `This file is not supported`;
      }
      return `Drop it like it's hot!`;
    }
  }

  protected handleMouseEnter() {
    this.hovering = true;
  }

  protected handleMouseLeave() {
    this.hovering = false;
  }

  protected handleDragStart(event: DragEvent) {
  }

  protected handleDragEnd(event: DragEvent) {
    this.dragging = false;
  }

  protected get rippleStyle() {
    return this.style;
  }
  private setRippleStyle(element: HTMLElement, x: number, y: number) {
    const boundingRectangle: ClientRect = element.getBoundingClientRect();
    const size = Math.max(boundingRectangle.width, boundingRectangle.height);
    this.style.height = `${size}px`;
    this.style.width = `${size}px`;
    this.style.top = `${y - boundingRectangle.top - size/2}px`;
    this.style.left = `${x - boundingRectangle.left - size/2}px`;
  }

  protected handleDragEnter(event: DragEvent) {
    if(!event.dataTransfer) {
      return;
    }

    this.dragging = true;
    const element: HTMLElement = event.target as HTMLElement;
    this.setRippleStyle(element, event.pageX, event.pageY);

    if(event.dataTransfer.items && event.dataTransfer.items.length && this.accepted.length) {
      const file = Array.from(event.dataTransfer.items)[0];
      this.invalidFileType = !this.accepted.includes(file.type.toString());
    }
  }

  protected handleDragOver(event: DragEvent) {
    this.dragging = true;
  }

  protected handleDragLeave(event: DragEvent) {
    this.reset();
  }

  protected handleDrop(event: DragEvent) {
    if(this.invalidFileType) {
      this.reset();
      return;
    }

    this.dragging = false;
    if (!event.dataTransfer) {
      return;
    }

    const files: File[] = Array.from(event.dataTransfer.files);
    this.isUploading = true;
    if (this.multiple) {
      files.forEach((file: File) => {
        this.$emit('itemDropped', file);
      });
    } else {
      this.$emit('itemDropped', files[0]);
    }
  }

  protected removeFile() {
    this.$emit('itemRemoved');
  }

  public reset() {
    this.dragging = false;
    this.hovering = false;
    this.isUploading = false;
    this.invalidFileType = false;
    this.showRipple = false;
    this.isUploaded = false;
  }

  public uploaded() {
    if(this.clearAfterUpload) {
      this.isUploading = false;
      this.isUploaded = true;

      setTimeout(() => {
        this.reset();
      }, 1000)
      return;
    }

    this.isUploaded = true;
    this.isUploading = false;
  }


  @Watch('filePath')
  protected filePathChanged() {
    if(this.filePath && !this.clearAfterUpload) {
      this.filePathClone = this.filePath;
    }
  }
}

interface RippleStyle {
  top?: string;
  left?: string;
  height?: string;
  width?: string;
}
